import React, {useEffect, useState, useCallback} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import AttachmentsContainer from "@/components/AttachmentsContainer";
import Loading from "@/components/Loading";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import VLFModal from "@/components/Modal";
import HandleTagModal from "@/pages/Video/HandleTagModal";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import {PlaylistAPI} from "@/api/playlist";
import {AttachmentAPI} from "@/api/attachment";

import {
	dateFormat,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	videoTagTypeOptions,
} from "@/utils/constants";
import {usePlayers} from "@/utils/hooks";

import "./index.scss";
import {useTranslation} from "react-i18next";

// ── Video embed helpers ───────────────────────────────────────────────────────

const getYouTubeId = (url) => {
	if (!url) return null;
	const match = url.match(
		/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/,
	);
	return match ? match[1] : null;
};

const getVimeoId = (url) => {
	if (!url) return null;
	const match = url.match(/vimeo\.com\/(\d+)/);
	return match ? match[1] : null;
};

const fmtTime = (min, sec) => {
	const m = String(min || 0).padStart(2, "0");
	const s = String(sec || 0).padStart(2, "0");
	return `${m}:${s}`;
};

const TAG_COLORS = {
	goal: "#16a34a",
	shot: "#2563eb",
	assist: "#7c3aed",
	foul: "#dc2626",
	corner: "#d97706",
	dangerous_action: "#ea580c",
	substitution: "#0891b2",
	save: "#059669",
	tactical: "#64748b",
	other: "#94a3b8",
};

// ── Component ─────────────────────────────────────────────────────────────────

function VideoPage(props) {
	const {handleNotification} = props;

	const type = new URLSearchParams(useLocation().search).get("type");

	const {id} = useParams();
	const navigate = useNavigate();
	const {t} = useTranslation();

	const [loading, setLoading] = useState(false);

	const {players} = usePlayers({
		setLoading,
		handleNotification,
		goalkeepers: false,
		filtered: true,
		prospect: false,
	});

	const [attachments, setAttachments] = useState([null]);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [playlistData, setPlaylistData] = useState({});
	const [openTagModal, setOpenTagModal] = useState(false);
	const [editingTag, setEditingTag] = useState(null);
	const [videoStartTime, setVideoStartTime] = useState(0);

	const getPlaylist = async () => {
		setLoading(true);
		try {
			const response = await PlaylistAPI.getById(id);
			response.player =
				players.filter((p) => p.id === response.playerId)?.[0] || {};
			setPlaylistData(response);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getAttachments = async () => {
		setLoading(true);
		try {
			const response = await AttachmentAPI.getByTypeId("playlist", id);
			setAttachments(response);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (players?.length) {
			getPlaylist();
			getAttachments();
		}
	}, [players]); // eslint-disable-line react-hooks/exhaustive-deps

	const deletePlaylist = async () => {
		setLoading(true);
		try {
			await PlaylistAPI.delete(id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/video" + (type ? "?type=" + type : ""));
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	// ── Tag handlers ──────────────────────────────────────────────────────────

	const handleCreateTag = async (data) => {
		try {
			setLoading(true);
			await PlaylistAPI.createTag(id, data);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenTagModal(false);
			setEditingTag(null);
			getPlaylist();
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleUpdateTag = async (data, tagId) => {
		try {
			setLoading(true);
			await PlaylistAPI.updateTag(id, tagId, data);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenTagModal(false);
			setEditingTag(null);
			getPlaylist();
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteTag = async (tagId) => {
		try {
			setLoading(true);
			await PlaylistAPI.deleteTag(id, tagId);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlaylist();
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleTagConfirm = (data, tagId) => {
		if (tagId) {
			handleUpdateTag(data, tagId);
		} else {
			handleCreateTag(data);
		}
	};

	const jumpToTime = useCallback((min, sec) => {
		const totalSeconds = (parseInt(min) || 0) * 60 + (parseInt(sec) || 0);
		setVideoStartTime(totalSeconds);
	}, []);

	// ── Video embed ───────────────────────────────────────────────────────────

	const videoUrl = playlistData?.videoUrl;
	const youtubeId = getYouTubeId(videoUrl);
	const vimeoId = getVimeoId(videoUrl);
	const hasEmbed = youtubeId || vimeoId;

	const renderEmbed = () => {
		if (youtubeId) {
			return (
				<iframe
					key={`yt-${youtubeId}-${videoStartTime}`}
					src={`https://www.youtube.com/embed/${youtubeId}?start=${videoStartTime}&autoplay=${videoStartTime > 0 ? 1 : 0}&rel=0`}
					width="100%"
					height="450"
					frameBorder="0"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
					allowFullScreen
					style={{borderRadius: 10, background: "#000"}}
				/>
			);
		}
		if (vimeoId) {
			return (
				<iframe
					key={`vm-${vimeoId}-${videoStartTime}`}
					src={`https://player.vimeo.com/video/${vimeoId}#t=${videoStartTime}s`}
					width="100%"
					height="450"
					frameBorder="0"
					allow="autoplay; fullscreen; picture-in-picture"
					allowFullScreen
					style={{borderRadius: 10, background: "#000"}}
				/>
			);
		}
		return null;
	};

	// ── Tags sorted by time ───────────────────────────────────────────────────

	const tags = (playlistData?.tags || []).sort((a, b) => {
		const aTime = (a.minute || 0) * 60 + (a.second || 0);
		const bTime = (b.minute || 0) * 60 + (b.second || 0);
		return aTime - bTime;
	});

	// ── Page config ───────────────────────────────────────────────────────────

	const mainConfiguration = [
		{
			value:
				playlistData?.creationDate &&
				moment(playlistData?.creationDate).format(dateFormat),
			label: "Data",
			size: "col-6 col-lg-3",
		},
		{value: playlistData?.name, label: t("name"), size: "col-6 col-lg-3"},
		{
			value: playlistData?.playlistType,
			label: "Tipologia",
			size: "col-6 col-lg-3",
		},
		{
			value: playlistData?.categories?.length ? (
				<div className="categories-list">
					{playlistData.categories.map((c, k) => (
						<div className="categories-element" key={k}>
							{c.playersCategory}
						</div>
					))}
				</div>
			) : playlistData?.player ? (
				playlistData?.player?.firstName + " " + playlistData?.player?.lastName
			) : (
				"---"
			),
			label: playlistData?.categories?.length ? "Categorie" : "Giocatore",
			size: "col-6 col-lg-3",
		},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/video" + (type ? "?type=" + type : "")),
			color: "info",
		},
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () =>
				navigate("/video/" + id + "/edit" + (type ? "?type=" + type : "")),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => setOpenHandleConfirm(true),
			color: "error",
		},
	];

	const breadcrumbsElements = [
		{label: "Video", path: "/video" + (type ? "?type=" + type : "")},
		{label: "/"},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={playlistData ? playlistData?.name || "" : ""}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin col-container">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={mainConfiguration} />
									</div>
								</div>
							}
							openable={false}
						/>
					</div>

					{/* ── Video embed ──────────────────────────────── */}
					{hasEmbed && (
						<div className="col-sm-12 padding-small mt-2">
							<VLFCard
								title="Video"
								content={renderEmbed()}
								openable={false}
							/>
						</div>
					)}

					{/* ── Tags / Timeline ──────────────────────────── */}
					{hasEmbed && (
						<div className="col-sm-12 padding-small mt-2">
							<VLFCard
								title="Tag video"
								content={
									<div className="vt">
										<div className="vt__actions">
											<button
												className="vt__add-btn"
												onClick={() => {
													setEditingTag(null);
													setOpenTagModal(true);
												}}
											>
												<AddIcon sx={{fontSize: 16}} />
												Aggiungi tag
											</button>
										</div>

										{tags.length > 0 ? (
											<div className="vt__timeline">
												{tags.map((tag, i) => (
													<div
														key={tag.id || i}
														className="vt__tag"
														onClick={() => jumpToTime(tag.minute, tag.second)}
													>
														<div
															className="vt__tag-dot"
															style={{background: TAG_COLORS[tag.tagType] || TAG_COLORS.other}}
														/>
														<div className="vt__tag-time">
															{fmtTime(tag.minute, tag.second)}
														</div>
														<div className="vt__tag-info">
															<span
																className="vt__tag-type"
																style={{color: TAG_COLORS[tag.tagType] || TAG_COLORS.other}}
															>
																{tag.tagLabel || videoTagTypeOptions.find((o) => o.value === tag.tagType)?.label || tag.tagType}
															</span>
															{tag.playerName && (
																<span className="vt__tag-player">{tag.playerName}</span>
															)}
															{tag.notes && (
																<span className="vt__tag-notes">{tag.notes}</span>
															)}
														</div>
														<div className="vt__tag-actions">
															<button
																className="vt__icon-btn"
																onClick={(e) => {
																	e.stopPropagation();
																	jumpToTime(tag.minute, tag.second);
																}}
																title="Vai al momento"
															>
																<PlayArrowIcon sx={{fontSize: 16}} />
															</button>
															<button
																className="vt__icon-btn"
																onClick={(e) => {
																	e.stopPropagation();
																	setEditingTag(tag);
																	setOpenTagModal(true);
																}}
																title="Modifica"
															>
																<EditIcon sx={{fontSize: 14}} />
															</button>
															<button
																className="vt__icon-btn vt__icon-btn--delete"
																onClick={(e) => {
																	e.stopPropagation();
																	handleDeleteTag(tag.id);
																}}
																title="Elimina"
															>
																<CloseIcon sx={{fontSize: 14}} />
															</button>
														</div>
													</div>
												))}
											</div>
										) : (
											<div className="vt__empty">
												Nessun tag. Guarda il video e aggiungi i momenti importanti.
											</div>
										)}
									</div>
								}
								openable={false}
							/>
						</div>
					)}

					{/* ── Attachments ──────────────────────────────── */}
					<div className="col-sm-12 padding-small mt-2">
						<VLFCard
							title="Multimedia"
							content={
								<div className="row">
									<div className="col-sm-12">
										<AttachmentsContainer
											entityId={id}
											entityType="playlist"
											attachments={attachments}
											handleNotification={handleNotification}
											editable={false}
										/>
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
			</div>

			{/* ── Modals ──────────────────────────────────────── */}
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={deletePlaylist}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>

			{openTagModal && (
				<HandleTagModal
					open={openTagModal}
					close={() => {
						setOpenTagModal(false);
						setEditingTag(null);
					}}
					confirm={handleTagConfirm}
					players={players}
					initialData={editingTag}
				/>
			)}
		</Page>
	);
}

export default VideoPage;
